import { CommonModule } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { NavigationEnd, Router, RouterOutlet } from '@angular/router'
import { Store } from '@ngrx/store'
import { SimpleNotificationsModule } from 'angular2-notifications'
import { KeycloakProfile } from 'keycloak-js'
import { PrimeNG } from 'primeng/config'
import { from, of, Subscription } from 'rxjs'
import { catchError, filter, tap } from 'rxjs/operators'
import { $app } from './app.service'
import { AppAuthService } from './shared/services/app-auth.service'
import { FooterbarComponent } from './core/footerbar/footerbar.component'
import { NavbarComponent } from './core/navbar/navbar.component'
import { NavbarService } from './core/navbar/navbar.service'
import { StatusTopbarComponent } from './core/status-topbar/status-topbar.component'
import { TopbarComponent } from './core/topbar/topbar.component'
import { AppUserInfoFromKC } from './shared/models/interfaces/app-user-info-from-kc.interface'
import { AppThemeService } from './shared/services/app-theme.service'
import { AppSpinnerService } from './shared/services/app-spinner.service'
import { ProjectService } from './shared/store/project/project.service'
import * as StageActions from './shared/store/stage/stage.actions'
import { getProject, getUser, selectIsSidebarOpen } from './shared/store/stage/stage.selectors'
import { AppState } from './shared/store/storage'

@Component({
   selector: 'app-root',
   templateUrl: './app.component.html',
   providers: [ProjectService],
   standalone: true,
   imports: [CommonModule, StatusTopbarComponent, FooterbarComponent, NavbarComponent, TopbarComponent, RouterOutlet, SimpleNotificationsModule]
})
export class AppComponent implements OnInit {
   project$ = $app.getCurrentProject()

   user$ = this._store.select(getUser)

   isSidebarOpen$ = this._store.select(selectIsSidebarOpen)

   console: boolean = $app.core.modeDevel
   logPrefix: string = 'src/app/ - ' + this.constructor.name

   $app = $app

   public isError?: boolean

   private isStartupLoading = true

   private _user?: AppUserInfoFromKC

   private _sub = new Subscription()

   constructor(
      private _store: Store<AppState>,
      public auth: AppAuthService,
      private _router: Router,
      private appNavService: NavbarService,
      private themeService: AppThemeService,
      private primeNGConfig: PrimeNG,
      private spinnerService: AppSpinnerService
   ) {}

   ngOnInit() {
      if (this.console) console.log(this.logPrefix, 'ngOnInit')

      this.spinnerService.startupShow()

      this.primeNGConfig.overlayOptions = {
         styleClass: '',
         contentStyleClass: '',
         autoZIndex: true,
         baseZIndex: 45000
      }

      // waiting for first NavigationEnd
      const navEndSub = this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
         const navEnd = event as NavigationEnd
         const match = navEnd.urlAfterRedirects.match(/\/(\d+)(\/|$)/)
         const projectId = match ? match[1] : null

         $app.setProjectId(projectId ? Number(projectId) : undefined)
         this.onAfterNavigationEnd()

         if (!projectId || navEnd.urlAfterRedirects.includes('error')) {
            this.finishStartupLoading()
         }

         navEndSub.unsubscribe()
      })

      this._sub.add(
         this.auth.licenceSubject$.subscribe((hasLicence) => {
            if (!hasLicence) {
               this.accessDenied()
            } else {
               this.auth.retriveRole().then((role) => {
                  if (this._router.url.includes('access-denied') || role != this._user?.role) {
                     window.location.reload()
                  }
               })
            }
         })
      )

      this._sub.add(
         this._router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
               this.isError = event.url.includes('error') || event.urlAfterRedirects.includes('error')

               if (this.isError) return
               this.appNavService.retriveActiveItem()
            }
         })
      )

      this._sub.add(this._store.select(getUser).subscribe((user) => (this._user = user)))
   }

   ngOnDestroy(): void {
      this._sub.unsubscribe()
   }

   setupData() {
      this._store.dispatch(StageActions.loadCountries())
   }

   private onAfterNavigationEnd() {
      if (this.auth.hasLicence()) {
         this.themeService.retriveProjectTheme()
         this.setUser()
         this._store.dispatch(StageActions.setStatuses())

         this._sub.add(
            this._store.select(getProject).subscribe((proj) => {
               if (!proj) {
                  let currentProjectId = $app.getCurrentProjectId()
                  if (currentProjectId) {
                     this._store.dispatch(StageActions.setProject({ projectId: currentProjectId }))
                  }

                  return
               }

               this.finishStartupLoading()
               this.setupData()
            })
         )
      } else {
         this.finishStartupLoading()
         this.accessDenied()
      }
   }

   private finishStartupLoading() {
      if (!this.isStartupLoading) return

      this.isStartupLoading = false
      this.spinnerService.startupHide()
   }

   private setUser() {
      const mn = this.setUser.name
      from(this.auth.keycloakAngular.loadUserProfile())
         .pipe(
            filter((res) => res != null && res != undefined),
            tap((res) => {
               if (this.console) console.log(this.logPrefix, mn, 'Keycloack User profile res:', res)
            }),
            catchError((error) => {
               if (this.console) console.error(this.logPrefix, mn, 'Cannot retrive user info...')
               return of(error)
            })
         )
         .subscribe((user: KeycloakProfile) => {
            this.auth.retriveRole().then((role) => {
               this._store.dispatch(StageActions.setUser({ user: { name: `${user.firstName} ${user.lastName}`, email: user.email, role: role } }))
            })
         })
   }

   private accessDenied() {
      if (this.console) console.log(this.logPrefix, 'user does not have licence...')
      $app.core.goToSearchProjects()
   }
}
